// The quick keys row (carry bag, gift wrap, alteration, other stores) and its
// inline editor. The store's keys come from its POS config, and a counter may
// keep its own set unless the config says not; see utils/fashion/quickKeys.
import React, { useState } from "react";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import { MAX_KEYS, configQuickKeys, loadQuickKeys, newKeyId, resetQuickKeys, saveQuickKeys } from "../../../../utils/fashion/quickKeys";

const KIND_HINT = { panel: "Opens a screen", stores: "Stock in other stores for the selected line" };

// priceOf(key) → the price text for an item key ("₹10"), or "" when it has no item on this till.
export default function FashionQuickKeys({ onKey, disabled = false, priceOf }) {
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const [saved, setSaved] = useState(null);
  const keys = saved || loadQuickKeys(globalThis.localStorage, posConfig);
  const { canEdit } = configQuickKeys(posConfig);
  const [draft, setDraft] = useState(null);
  const shown = keys.filter((k) => !k.hidden);

  if (draft) {
    const edit = (id, patch) => setDraft((d) => d.map((k) => (k.id === id ? { ...k, ...patch } : k)));
    return (
      <div className="fp-qkeys-edit" role="group" aria-label="Edit quick keys">
        <p className="fp-muted fp-qkeys-note">Keys are saved on this till. An item key adds the product with that item code or barcode. Reset brings back the store's keys.</p>
        <ul className="fp-qkeys-rows">
          {draft.map((k) => (
            <li key={k.id} className="fp-qkeys-row">
              <input className="fp-input fp-qkeys-label" value={k.label} maxLength={24} aria-label="Key label" onChange={(e) => edit(k.id, { label: e.target.value })} />
              {k.kind === "product" ? (
                <input className="fp-input fp-qkeys-code" value={k.code} placeholder="Item code or barcode" aria-label={`Item code for ${k.label}`} onChange={(e) => edit(k.id, { code: e.target.value })} />
              ) : (
                <span className="fp-muted fp-qkeys-code">{KIND_HINT[k.kind]}</span>
              )}
              <label className="fp-qkeys-show">
                <input type="checkbox" checked={!k.hidden} onChange={(e) => edit(k.id, { hidden: !e.target.checked })} />
                Show
              </label>
              {k.id.startsWith("key-") && (
                <button type="button" className="fp-link" aria-label={`Remove ${k.label}`} onClick={() => setDraft((d) => d.filter((x) => x.id !== k.id))}>
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="fp-qkeys-foot">
          {draft.length < MAX_KEYS && (
            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setDraft((d) => [...d, { id: newKeyId(d), kind: "product", label: "New item", code: "", match: "" }])}>
              Add item key
            </button>
          )}
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setDraft(resetQuickKeys(globalThis.localStorage, posConfig))}>
            Reset to default
          </button>
          <span className="fp-qkeys-gap" />
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setDraft(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="fp-btn-primary fp-btn-sm"
            onClick={() => {
              setSaved(saveQuickKeys(draft.filter((k) => String(k.label).trim())));
              setDraft(null);
            }}
          >
            Save keys
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fp-qkeys" role="group" aria-label="Quick keys">
      {shown.map((k) => (
        <button key={k.id} type="button" className={`fp-qkey is-${k.kind}`} disabled={disabled} onClick={() => onKey(k)}>
          {k.label}
          {k.kind === "product" && priceOf?.(k) && <span className="fp-qkey-price fp-num">{priceOf(k)}</span>}
        </button>
      ))}
      {canEdit && (
        <button type="button" className="fp-qkey is-edit" disabled={disabled} onClick={() => setDraft(keys)} title="Change these keys">
          Edit keys
        </button>
      )}
    </div>
  );
}
